import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/shared/forms';
import { useViewer } from '@/entities/viewer';
import { addDays, isClock, todayIn } from '@/shared/lib/calendar';
import { formatDateTime, zonedToInstant } from '@/shared/lib/datetime';
import { Button, DateField, TextField, TimeField } from '@/shared/ui';

import { useCreateLink, useRevokeLink, useScopeLinks } from './board-api';
import { grantedRoleLabel, toRoleInput, type WorkspaceRoleChoice } from './roles';
import type { CustomRole, MemberRole } from './types';
import { joinUrl } from './join-url';
import styles from './Collab.module.css';
import { RoleSelect, WorkspaceRoleSelect } from './RoleSelect';

type Scope = { kind: 'workspace'; id: string } | { kind: 'board'; id: string };

const MIN_HOURS = 1;
const MAX_DAYS = 90;
const MAX_USES = 1000;
const DEFAULT_TIME = '12:00';

type ExpiryError = 'dateRequired' | 'timeInvalid' | 'timeMissing' | 'past' | 'tooSoon' | 'tooFar';

function checkExpiry(
  date: string,
  time: string,
  zone: string,
  now: number,
): { instant: Date | null; error: ExpiryError | null } {
  if (date === '') return { instant: null, error: 'dateRequired' };
  if (time === '') return { instant: null, error: 'timeMissing' };
  const instant = isClock(time) ? zonedToInstant(date, time, zone) : null;
  if (instant === null) return { instant: null, error: 'timeInvalid' };
  const hours = (instant.getTime() - now) / 3_600_000;
  if (hours <= 0) return { instant, error: 'past' };
  if (hours < MIN_HOURS) return { instant, error: 'tooSoon' };
  if (hours > MAX_DAYS * 24) return { instant, error: 'tooFar' };
  return { instant, error: null };
}

/**
 * Time-limited, use-limited invitation links. The secret is shown ONCE (only its hash is
 * stored); active links list their remaining validity and uses and can be revoked.
 */
export function LinksPanel({
  scope,
  allowedRoles,
  customRoles,
}: {
  scope: Scope;
  allowedRoles: MemberRole[];
  /** Workspace links only (see `InvitePanel`). */
  customRoles?: readonly CustomRole[];
}) {
  const { t, i18n } = useTranslation();
  const links = useScopeLinks(scope);
  const create = useCreateLink(scope);
  const revoke = useRevokeLink();
  const [choice, setChoice] = useState<WorkspaceRoleChoice>({
    role: allowedRoles.at(-1) ?? 'member',
    customRoleId: null,
  });
  const viewer = useViewer();
  const zone =
    (viewer.status === 'authenticated' ? viewer.user.timezone : null) ??
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [date, setDate] = useState(() => addDays(todayIn(zone), 7));
  const [time, setTime] = useState(DEFAULT_TIME);
  const [limit, setLimit] = useState('5');
  const [approval, setApproval] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [now] = useState(() => Date.now());
  const { instant, error: expiryError } = checkExpiry(date, time, zone, now);
  const uses = Number(limit);
  const limitValid = /^\d+$/.test(limit) && uses >= 1 && uses <= MAX_USES;
  const ready = expiryError === null && limitValid && instant !== null;

  const submit = () => {
    if (!ready) return;
    create.mutate(
      {
        ...toRoleInput(choice),
        expires_at: instant.toISOString(),
        max_uses: uses,
        requires_approval: approval,
      },
      {
        onSuccess: (result) => {
          setCreated(joinUrl(result.token));
          setCopied(false);
        },
      },
    );
  };

  return (
    <section className={styles.stack} aria-label={t('collab.links.title')}>
      <div className={styles.inline}>
        <label>
          <span>{t('collab.invite.role')}</span>
          {customRoles === undefined ? (
            <RoleSelect
              label={t('collab.invite.role')}
              value={choice.role ?? 'member'}
              roles={allowedRoles}
              size="md"
              onChange={(role) => {
                setChoice({ role, customRoleId: null });
              }}
            />
          ) : (
            <WorkspaceRoleSelect
              label={t('collab.invite.role')}
              value={choice}
              standardRoles={allowedRoles}
              customRoles={customRoles}
              onChange={setChoice}
            />
          )}
        </label>
        <DateField
          label={t('collab.links.expiryDate')}
          value={date}
          onChange={setDate}
          invalid={expiryError !== null && expiryError !== 'timeMissing'}
        />
        <TimeField
          label={t('collab.links.expiryTime')}
          value={time}
          onChange={setTime}
          invalid={expiryError === 'timeInvalid' || expiryError === 'timeMissing'}
        />
        <TextField
          label={t('collab.links.limit')}
          type="number"
          inputMode="numeric"
          min={1}
          max={MAX_USES}
          step={1}
          value={limit}
          error={
            limitValid || limit === ''
              ? undefined
              : t('collab.links.errors.limit', { max: MAX_USES })
          }
          onChange={(event) => {
            setLimit(event.target.value);
          }}
        />
        <label className={styles.permission}>
          <input
            type="checkbox"
            checked={approval}
            onChange={(event) => {
              setApproval(event.target.checked);
            }}
          />
          <span>{t('collab.links.requiresApproval')}</span>
        </label>
        <Button variant="primary" disabled={create.isPending || !ready} onClick={submit}>
          {t('collab.links.create')}
        </Button>
      </div>
      <p
        className={expiryError ? styles.fieldError : styles.muted}
        role={expiryError ? 'alert' : 'status'}
      >
        {expiryError
          ? t(`collab.links.errors.${expiryError}`, { days: MAX_DAYS, hours: MIN_HOURS })
          : instant &&
            t('collab.links.expiresPreview', {
              when: formatDateTime(instant.toISOString(), i18n.language, zone),
              zone,
            })}
      </p>
      <FormError error={create.error} />
      {created && (
        <div className={styles.secret} role="status" data-testid="new-link">
          <code>{created}</code>
          <Button
            size="sm"
            onClick={() => {
              void navigator.clipboard.writeText(created).then(() => {
                setCopied(true);
              });
            }}
          >
            {copied ? t('collab.links.copied') : t('collab.links.copy')}
          </Button>
          <span className={styles.muted}>{t('collab.links.shownOnce')}</span>
        </div>
      )}
      <h4>{t('collab.links.active')}</h4>
      {(links.data ?? []).length === 0 ? (
        <p className={styles.muted}>{t('collab.links.none')}</p>
      ) : (
        <ul className={styles.list}>
          {(links.data ?? []).map((link) => (
            <li key={link.id} className={styles.row} data-testid="active-link">
              <span className={styles.rowMain}>
                <span>
                  {grantedRoleLabel(t, link.role, link.custom_role_name)}
                  {link.requires_approval && ` · ${t('collab.links.needsApproval')}`}
                </span>
                <span className={styles.rowMeta}>
                  {t('collab.links.until', {
                    when: formatDateTime(link.expires_at, i18n.language),
                  })}{' '}
                  · {t('collab.links.uses', { used: link.uses_count, max: link.max_uses })}
                </span>
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={revoke.isPending}
                onClick={() => {
                  revoke.mutate(link.id);
                }}
              >
                {t('collab.links.revoke')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
